import { get, isEqual } from 'lodash';
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { ActionType } from 'react-table';

import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { trackSegment } from '~/components/SegmentAnalytics';
import DocumentDetailsPanel from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { Nullable } from '~/eds/src/types/Nullable';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { parseDate } from '~/eds/src/utils/parseDate';
import { TableContextType } from '~/enums/TableContextType';
import { useAddToGroup } from '~/features/groups/hooks/useAddToGroup';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermission } from '~/hooks/usePermission';
import { useTableSettings } from '~/hooks/useTableSettings';
import { testIsPreBuiltAdmin } from '~/permissions/utils/testIsPreBuiltAdmin';
import { api } from '~/redux/api/createApi';
import { ViewSetField } from '~/redux/api/methods/postSigDocument';
import { ColumnSortOrder } from '~/types/Columns';
import { FolderData } from '~/types/Document';
import {
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP,
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
  MODAL_FOLDER_TREE,
  MODAL_MULTI_EDIT,
} from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { User } from '~/types/User';
import { getValidEntity } from '~/utils/object';

type ConfigurableColumnState = {
  pageSize: number;
  page: number;
  sortBy: ColumnSortOrder;
};
interface Props {
  data: FolderData[];
  isLoading: boolean;
  onNavigate: (params: { type: FolderData['type'] }) => void;
  tableState: ConfigurableColumnState & {
    totalCount: number;
  };
  onUpdateTableState: (state: ConfigurableColumnState) => void;
  /** to reuse all permission and checkings made on the parent component, this component
   * will receive the menuItem for the legacu table and map it to eds.MenuAction
   */
  getMenuItems: (
    item: FolderData,
    currentUser: User,
  ) => {
    content: string;
    contentString?: string;
    onClick: () => void;
    disabled?: boolean;
    tooltip?: string;
  }[];
  handleShowModal: (
    modalType: string,
    selectedItems?: FolderData[],
    modalBulkAction?: string,
  ) => void;
  refetchData: () => void;
  onSelectAllClick: (isSelectAll: boolean) => void;
}

function _DocumentsTableV2(
  {
    data,
    isLoading,
    tableState,
    getMenuItems,
    handleShowModal,
    onNavigate,
    onSelectAllClick,
    onUpdateTableState,
    refetchData,
  }: Props,
  ref: any,
) {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const [dataMap, setDataMap] = useState<Record<EdsPilotId, FolderData>>({});
  const isDetailsPanelEnabled = useFlag(FlagType.UnifiedDocumentDetailsPanel);
  const disableDocumentTableSorting = useFlag(
    FlagType.DisableDocumentTableSorting,
  );
  const folderContentPrefetch = useFlag(FlagType.FolderContentPrefetch);
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const {
    panel: addToGroupPanel,
    open: openAddToGroup,
    addToGroupBulkAction,
    addToGroupRowAction,
    canAddToGroup: canEditEntityGroups,
  } = useAddToGroup('document');
  const canAddToGroup = hasGroupedTicketAndDocs && canEditEntityGroups;
  const disableSorting = disableDocumentTableSorting || folderContentPrefetch;
  const { hasPermission: hasDocumentGroupPermission } = usePermission({
    permission: {
      resourceId: 'document_groups',
      resourceType: 'view',
    },
  });
  const [activeDocument, setActiveDocument] =
    useState<Nullable<FolderData>>(null);
  const { data: fieldsResponse } =
    api.endpoints.getDocumentsViewsetFields.useQuery({
      hasDocumentGroupPermission: !!hasDocumentGroupPermission,
    });

  const { page, pageSize, sortBy, totalCount } = tableState;

  const { data: tableColumns, isFetching: isColumnsFetching } =
    api.endpoints.getCurrentTableView.useQuery({
      context: undefined,
    });

  const {
    tableSettings = {
      columnOrder: [],
      pageSize,
      sortBy,
      columnWidths: {},
    },
    updateTableSettings,
  } = useTableSettings(TableContextType.Documents);

  const {
    columnOrder,
    columnWidths,
    sortBy: sortByTableSetting,
  } = tableSettings;

  useEffect(() => {
    if (tableColumns) {
      const dynamicColumns = tableColumns.map((el) => `${el.field_id}`);
      updateTableSettings({
        ...tableSettings,
        columnOrder: ['name', ...dynamicColumns],
      });
    }
  }, [tableColumns]);

  const derivedcolumnOrder = useMemo(() => {
    if (tableColumns) {
      return ['name', ...tableColumns.map((el) => `${el.field_id}`)];
    }
    return ['name'];
  }, [tableColumns]);

  const [updateTableView, { isLoading: isUpdateTableViewLoading }] =
    api.endpoints.updateTableView.useMutation();

  const hardcodedColumns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'DocumentsTableV2.nameColumn',
        defaultMessage: 'Name',
        description: 'Name column header',
      }),
      minWidth: 'm',
      cellType: 'filesystem',
      mapCellProps: (item: FolderData) => ({
        onClick: () => onNavigate({ type: item.type }),
        folder:
          item.type === 'folder'
            ? {
                id: item.id,
                name: item.name,
                link: { pathname: `/documents/${item.id}` },
                path: '',
              }
            : undefined,
        file:
          item.type === 'document'
            ? {
                name: `${item.name}${item.fileType}`,
                link: { pathname: `/document/${item.id}` },
              }
            : undefined,
      }),
    },
  ];

  const { isAllSelected, selectedIds, onTableUpdate, clear } =
    useTableSelection();

  useImperativeHandle(ref, () => ({
    clearSelectedRows: () => {
      clear();
    },
  }));

  useEffect(() => {
    onSelectAllClick(isAllSelected);
  }, [isAllSelected]);

  useEffect(() => {
    if (data) {
      const newDataMap = Object.fromEntries(data.map((d) => [d.id, d]));
      setDataMap({ ...dataMap, ...newDataMap });
    }
  }, [data]);

  const columns = useMemo(() => {
    if (fieldsResponse) {
      const filteredFields = hasGroupedTicketAndDocs
        ? fieldsResponse.filter(
            (field) => field.fieldId !== 'document_group_id',
          )
        : fieldsResponse;

      return [
        ...hardcodedColumns,
        ...filteredFields.map((viewsetField) =>
          prepareTableColumns(viewsetField, data?.[0]),
        ),
      ].map((column) => ({
        ...column,
        disableSortBy: disableSorting,
      }));
    }
    return [];
  }, [fieldsResponse, hasGroupedTicketAndDocs, data]);

  function getBulkActions({ selectedRowIds }: any) {
    const isAnyFolderSelected = selectedRowIds.some(
      (id: string) => dataMap[id]?.type === 'folder',
    );

    const selectedItems = getValidEntity(dataMap, selectedRowIds);
    const isSomeNonMovableItemSelected = selectedItems.some(
      (folderData: FolderData) => !folderData.isMovable,
    );
    const isSomeContainsSyncPairSelected = selectedItems.some(
      (folderData: FolderData) => folderData.contains_sync_pair,
    );
    return [
      !hasGroupedTicketAndDocs && {
        key: 'addToGroup',
        label: intl.formatMessage({
          id: 'DocumentsTableV2.addToGroupAction',
          defaultMessage: 'Add to Group',
          description: 'Action label for adding a document to a group',
        }),
        onClick: () => handleShowModal(MODAL_DOCUMENT_GROUP, selectedItems),
        disabled: isAnyFolderSelected,
        tooltip: isAnyFolderSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.addToGroupFolderTooltip',
              defaultMessage:
                "To Add to Group, make sure your selection doesn't include any folders.",
              description:
                'Tooltip shown when folders are selected for add to group action',
            })
          : '',
      },
      canAddToGroup && {
        key: 'addToGroup',
        ...addToGroupBulkAction(
          selectedRowIds.map((id: string) =>
            String(dataMap[id]?.document_id || ''),
          ),
        ),
        disabled: isAnyFolderSelected,
        tooltip: isAnyFolderSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.addToGroupFolderTooltip',
              defaultMessage:
                "To Add to Group, make sure your selection doesn't include any folders.",
              description:
                'Tooltip shown when folders are selected for add to group action',
            })
          : '',
      },
      {
        key: 'move',
        label: intl.formatMessage({
          id: 'DocumentsTableV2.moveAction',
          defaultMessage: 'Move',
          description: 'Bulk action to move documents',
        }),
        disabled: isSomeNonMovableItemSelected,
        tooltip: isSomeNonMovableItemSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.movePrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to move one or more of the items you have selected.',
              description: 'Tooltip shown when user lacks move privileges',
            })
          : '',
        onClick: () =>
          handleShowModal(MODAL_FOLDER_TREE, selectedItems, 'Move'),
      },
      {
        key: 'copy',
        label: intl.formatMessage({
          id: 'DocumentsTableV2.copyAction',
          defaultMessage: 'Copy',
          description: 'Bulk action to copy documents',
        }),
        disabled: isSomeNonMovableItemSelected,
        tooltip: isSomeNonMovableItemSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.copyPrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to copy one or more of the items you have selected.',
              description: 'Tooltip shown when user lacks copy privileges',
            })
          : '',
        onClick: () =>
          handleShowModal(MODAL_FOLDER_TREE, selectedItems, 'Copy'),
      },
      {
        key: 'edit',
        label: intl.formatMessage({
          id: 'DocumentsTableV2.editAction',
          defaultMessage: 'Edit',
          description: 'Bulk action to edit documents',
        }),
        disabled: isSomeNonMovableItemSelected,
        tooltip: isSomeNonMovableItemSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.editPrivilegeTooltip',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description: 'Tooltip shown when user lacks edit privileges',
            })
          : '',
        onClick: () => handleShowModal(MODAL_MULTI_EDIT, selectedItems, 'edit'),
      },
      {
        key: 'delete',
        label: intl.formatMessage({
          id: 'DocumentsTableV2.deleteAction',
          defaultMessage: 'Delete',
          description: 'Bulk action to delete documents',
        }),
        disabled:
          isSomeNonMovableItemSelected || isSomeContainsSyncPairSelected,
        tooltip: isSomeContainsSyncPairSelected
          ? intl.formatMessage({
              id: 'DocumentsTableV2.deleteSyncPairTooltip',
              defaultMessage:
                ' The selected item(s) contain synchronizations with external storage providers, please contact your Evisort admin to stop synchronization before deleting the selected item(s).',
              description:
                'Tooltip shown when sync pair items are selected for deletion',
            })
          : isSomeNonMovableItemSelected
            ? intl.formatMessage({
                id: 'DocumentsTableV2.deletePrivilegeTooltip',
                defaultMessage:
                  'You do not have privileges to delete one or more of the items you have selected.',
                description: 'Tooltip shown when user lacks delete privileges',
              })
            : '',
        onClick: () => handleShowModal(MODAL_DELETE, selectedItems, 'delete'),
      },
    ].filter(Boolean);
  }

  function getRowActions(item: FolderData) {
    const items = getMenuItems(item, currentUser);
    const isPreBuiltAdmin = testIsPreBuiltAdmin(currentUser);

    const shouldEnableMenuActions =
      isPreBuiltAdmin || item.user_visibility_level === 'OPEN';
    const shouldEnableDownlaod =
      item.user_visibility_level === 'OPEN_NO_EDIT' ||
      item.user_visibility_level === 'OPEN';

    const addToGroupAction =
      canAddToGroup && item.type !== 'folder'
        ? [
            {
              ...addToGroupRowAction,
              onClick: () =>
                openAddToGroup([
                  {
                    resourceType: 'document',
                    resourceId: String(item.document_id || ''),
                  },
                ]),
            },
          ]
        : [];

    return [
      ...addToGroupAction,
      ...items.map(({ content, contentString, onClick, disabled, tooltip }) => {
        const shouldEnableAction =
          content === 'Download'
            ? shouldEnableDownlaod
            : shouldEnableMenuActions;

        return {
          onClick,
          label: typeof content === 'string' ? content : contentString,
          disabled: !shouldEnableAction || disabled,
          tooltip: tooltip
            ? tooltip
            : !shouldEnableAction
              ? intl.formatMessage(
                  {
                    id: 'DocumentsTableV2.actionPrivilegeTooltip',
                    defaultMessage: 'You do not have privileges to {action}',
                    description:
                      'Generic tooltip for action privilege restriction. Example: action = "download"',
                  },
                  {
                    action:
                      typeof content === 'string' ? content : contentString,
                  },
                )
              : '',
        };
      }),
    ];
  }

  function onSaveView() {
    handleShowModal(MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE);
  }

  function onLoadView() {
    handleShowModal(MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH);
  }

  function onPageSelect({ pageIndex }: { pageIndex: number }) {
    onUpdateTableState({ ...tableState, page: pageIndex });
  }

  function onPageSizeChange(pageSize: number) {
    onUpdateTableState({ ...tableState, page: 1, pageSize });
  }

  function handleUpdate(state: any, action?: ActionType) {
    // Defer parent state updates to avoid "Cannot update component while rendering another"
    // (Table's state reducer runs during render). State stays in sync because Table already
    // has the new state; we only defer applying it in the parent.
    queueMicrotask(() => {
      onTableUpdate(state, action);
      if (action?.type === 'toggleSortBy') {
        onUpdateTableState({ ...tableState, sortBy: state.sortBy[0] });
      }
    });
  }

  async function onSetColumnOrder(newColumnOrder: string[]) {
    //Since name is a hardcoded column, it will always be included. we need to remove
    //from the viewset request.
    const dynamicColumns = newColumnOrder.filter((col) => col !== 'name');
    if (!isEqual(columnOrder, dynamicColumns)) {
      const newColumnOrderPayload = dynamicColumns.map((col, index) => ({
        field_id: isNaN(parseInt(col)) ? col : parseInt(col),
        column_number: index,
      }));
      await updateTableView({
        fields: newColumnOrderPayload,
        context: undefined,
      }).unwrap();
      refetchData();
    }
  }

  function onDetailsClick(document: FolderData) {
    setActiveDocument(document);
    trackSegment('selectOverlayPanel', {
      name: document.id,
    });
  }

  function onHideDatailsPanel() {
    trackSegment('selectClose', {
      name: activeDocument?.id,
    });
    setActiveDocument(null);
  }

  const isDetailsPanelNextDisabled = useMemo(() => {
    if (activeDocument) {
      /** data has ALL folders defined first and then files. we only
       * need to disable the next button if currentIndex is the last element of the table.
       */
      const currentIndex = data.indexOf(activeDocument);
      return currentIndex === data.length - 1;
    }
    return true;
  }, [data, activeDocument]);

  const isDetailsPanelPreviousDisabled = useMemo(() => {
    if (activeDocument) {
      /** data has ALL folders defined first and then files. we need to check
       * if the previous element is a folder or not. if it is a folder, we need to disable.
       */
      const currentIndex = data.indexOf(activeDocument);
      return data[currentIndex - 1]?.type === 'folder';
    }
    return true;
  }, [data, activeDocument]);

  const onDetailsPanelNextClick = () => {
    if (activeDocument) {
      const currentIndex = data.indexOf(activeDocument);
      setActiveDocument(data[currentIndex + 1]);
    }
  };

  const onDetailsPanelPreviousClick = () => {
    if (activeDocument) {
      const currentIndex = data.indexOf(activeDocument);
      setActiveDocument(data[currentIndex - 1]);
    }
  };

  return (
    <>
      <ContentContainer
        loadingContent={{
          isLoading:
            isColumnsFetching ||
            isUpdateTableViewLoading ||
            !isEqual(derivedcolumnOrder, columnOrder),
        }}
      >
        <PersistedTable
          name="folder-documents-results"
          context={TableContextType.Documents}
          totalCount={totalCount}
          columns={columns}
          isLoading={isLoading}
          data={data}
          activeRowId={activeDocument?.id}
          getBulkActions={getBulkActions}
          getRowActions={getRowActions}
          onPaginate={onPageSelect}
          onPageSizeChange={onPageSizeChange}
          onSetColumnOrder={onSetColumnOrder}
          onUpdate={handleUpdate}
          onSaveView={onSaveView}
          onLoadView={onLoadView}
          rowDetails={
            isDetailsPanelEnabled
              ? {
                  onClick: onDetailsClick,
                  condition: (item: FolderData) => item.type === 'document',
                }
              : undefined
          }
          state={{
            columnOrder: tableSettings.columnOrder,
            pageIndex: page,
            pageSize,
            sortBy: [sortByTableSetting],
            isAllRowsSelected: isAllSelected,
            selectedRowIds: selectedIds,
            columnWidths,
          }}
          options={{
            enableExportXlsx: false,
            enablePageSizeSelect: true,
            enableSelectRows: true,
            enableStickyHeader: true,
            stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
          }}
          reactTableOptions={{
            autoResetSortBy: false,
            disableSortRemove: true,
            manualSortBy: true,
          }}
          getRowActionsMenuId={(d: any, i: number) =>
            i === 0 ? OnboardingIdType.AccessSettingsTab : undefined
          }
        />
      </ContentContainer>
      {isDetailsPanelEnabled && (
        <DocumentDetailsPanel
          key={activeDocument?.id}
          docHandlerId={activeDocument?.id}
          onHide={onHideDatailsPanel}
          actionsConfig={{
            next: {
              onClick: onDetailsPanelNextClick,
              disabled: isDetailsPanelNextDisabled,
            },
            previous: {
              onClick: onDetailsPanelPreviousClick,
              disabled: isDetailsPanelPreviousDisabled,
            },
            rename: {
              onSuccess: refetchData,
            },
            delete: {
              onSuccess: refetchData,
            },
          }}
        />
      )}
      {addToGroupPanel}
    </>
  );
}

function getFieldDisplayAndType(
  item: FolderData,
  fieldId: PilotId,
): {
  displayValue: string;
  rawValues: string[] | undefined;
  searchType: string | undefined;
} {
  const docField = item.document_fields?.find(
    (field) => field.field_id === fieldId,
  );
  const rawValues = docField?.display_value;
  const displayValue = rawValues?.join(' , ') ?? '';
  const searchType = docField?.search_type;
  return { displayValue, rawValues, searchType };
}

function isDate(value: string): boolean {
  return !isNaN(Date.parse(value));
}

function getCellTypeFromSearchType(
  searchType: string | undefined,
): 'number' | 'datetime' | 'text' {
  switch (searchType) {
    case 'NUMBER':
      return 'number';
    case 'DATE':
      return 'datetime';
    default:
      return 'text';
  }
}

function prepareTableColumns(
  viewsetField: ViewSetField,
  firstRow: FolderData | undefined,
) {
  const { fieldId, fieldName } = viewsetField;
  const defaultAttributes = {
    key: `${fieldId}`,
    title: fieldName,
  };
  let columnTypeAttributes;
  if (fieldId === 'document_group_id') {
    columnTypeAttributes = {
      disableSortBy: true,
      cellType: 'link',
      minWidth: 'm',
      mapCellProps: (item: FolderData) => ({
        icon: 'groups',
        pathname: `/document-group/${get(item, 'groups[0].groupId')}`,
        text: get(item, 'groups[0].name', ''),
      }),
    };
  } else {
    const columnSearchType = firstRow
      ? getFieldDisplayAndType(firstRow, fieldId as number).searchType
      : undefined;
    columnTypeAttributes = {
      cellType: getCellTypeFromSearchType(columnSearchType),
      mapCellProps: (item: FolderData) => {
        const { displayValue, rawValues, searchType } = getFieldDisplayAndType(
          item,
          fieldId as number,
        );
        const datetime = isDate(displayValue)
          ? parseDate(displayValue)
          : undefined;
        return {
          text: displayValue,
          datetime,
          number:
            searchType === 'NUMBER' && rawValues?.[0] != null
              ? rawValues.map((v) => Number(v))
              : undefined,
        };
      },
    };
  }

  return { ...defaultAttributes, ...columnTypeAttributes };
}

export const DocumentsTableV2 = React.forwardRef(_DocumentsTableV2);
